import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../types';

export const axiosInstance: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
    withCredentials: true,
});

let refreshPromise: Promise<string> | null = null;

const getAccessToken = (): string | null => {
    return localStorage.getItem('access_token');
};

const setAccessToken = (token: string): void => {
    localStorage.setItem('access_token', token);
};

const removeAccessToken = (): void => {
    localStorage.removeItem('access_token');
};

function redirectToLogin(reason: string): void {
    console.warn('[auth] redirect to login:', reason);
    if (window.location.pathname !== '/login') {
        window.location.replace('/login');
    }
}

/** Один inflight refresh на вкладку — без гонки ротации refresh-токена. */
const refreshAccessToken = (): Promise<string> => {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = axiosInstance
        .post(
            '/auth/refresh',
            {},
            {
                skipAuthRefresh: true,
            } as InternalAxiosRequestConfig & { skipAuthRefresh?: boolean },
        )
        .then((response) => {
            const newAccessToken = response.data?.access_token;
            if (!newAccessToken || typeof newAccessToken !== 'string') {
                throw new Error('Refresh response has no access_token');
            }
            setAccessToken(newAccessToken);
            console.info('[auth] access token refreshed');
            return newAccessToken;
        })
        .catch((error) => {
            removeAccessToken();
            redirectToLogin('refresh failed');
            throw error;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        const token = getAccessToken();
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ message?: string }>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
            skipAuthRefresh?: boolean;
        };

        const requestUrl = originalRequest?.url || '';
        const isRefreshRequest =
            requestUrl.includes('/auth/refresh') ||
            requestUrl.endsWith('auth/refresh') ||
            originalRequest?.skipAuthRefresh === true;

        if (isRefreshRequest && error.response?.status === 401) {
            removeAccessToken();
            redirectToLogin('refresh returned 401');
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshRequest) {
            originalRequest._retry = true;
            try {
                const newAccessToken = await refreshAccessToken();
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }
                return await axiosInstance.request(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.message;
            switch (status) {
                case 400:
                    console.error('Bad Request:', message);
                    break;
                case 403:
                    // Не выкидываем на логин: 403 = нет прав, сессия может быть валидной
                    console.error('Forbidden:', message);
                    break;
                case 404:
                    console.error('Not Found:', message);
                    break;
                case 500:
                    console.error('Server Error:', message);
                    break;
                default:
                    console.error('Error:', message);
            }
        } else if (error.request) {
            console.error('Network Error:', error.message);
        } else {
            console.error('Error:', error.message);
        }

        return Promise.reject(error);
    },
);

export const authUtils = {
    getAccessToken,
    setAccessToken,
    removeAccessToken,
};

export default axiosInstance;
